import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/api';

const fmt = (v) => (v == null || Number.isNaN(v)
  ? '—'
  : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const Field = ({ label, children }) => (
  <div>
    <div className="text-2xs text-sap-textLight uppercase tracking-wider mb-0.5">{label}</div>
    <div className="text-sap-text break-words">{children ?? '—'}</div>
  </div>
);

/**
 * Read-only Customer BP summary from open sales orders (Card code).
 * For full vendor-style master (bank, dunning, etc.) use Purchasing → Vendors when the same code exists there.
 */
export default function CustomerBusinessPartnerOverview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = (searchParams.get('code') || '').trim();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!code) {
      setError('Missing customer code.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await api.get(`/business-partners/customer-extra?code=${encodeURIComponent(code)}`);
      setRow(data);
    } catch (e) {
      setRow(null);
      setError(e?.message || 'Could not load customer');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-sap-bg text-sap-textLight hover:text-sap-text transition-colors"
            title="Back"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div>
            <h1 className="page-title">Customer — Business Partner Master (from Sales)</h1>
            <p className="text-2xs text-sap-textLight">
              Aggregated from open sales orders for this Card code. Balance = open order line totals.
            </p>
          </div>
        </div>
      </div>

      {loading && <div className="text-xs text-sap-textLight px-2">Loading…</div>}
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
          {error.includes('endpoint') && (
            <span className="block mt-1 text-sap-textLight">
              Deploy the latest server code (including <code className="text-2xs">routes/businessPartners.js</code>) and restart Node.
            </span>
          )}
        </div>
      )}

      {!loading && row && (
        <div className="space-y-4 max-w-4xl">
          <div className="bg-white rounded-xl shadow-sm border border-sap-border/40 p-4">
            <div className="text-2xs font-bold text-sap-textLight uppercase tracking-wider mb-3 border-b border-sap-border/30 pb-1">General</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <Field label="Customer code"><span className="font-mono font-semibold text-sap-blue">{row.customer_ref_no || code}</span></Field>
              <Field label="Name">{row.customer_name}</Field>
              <Field label="Contact person">{row.contact_person}</Field>
              <Field label="Currency">{row.currency}</Field>
              <Field label="Tax ID (GST / federal)">{row.federal_tax_id}</Field>
              <Field label="Project (BP)">{row.bp_project}</Field>
              <Field label="Sales employee">{row.sales_employee}</Field>
              <Field label="Remarks">{row.remarks}</Field>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-sap-border/40 p-4">
            <div className="text-2xs font-bold text-sap-textLight uppercase tracking-wider mb-3 border-b border-sap-border/30 pb-1">Balance &amp; exposure</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <Field label="Open sales orders">{row.open_orders}</Field>
              <Field label="Open balance (line net)"><span className="tabular-nums font-semibold">{fmt(row.open_balance)}</span></Field>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-sap-border/40 p-4">
            <div className="text-2xs font-bold text-sap-textLight uppercase tracking-wider mb-3 border-b border-sap-border/30 pb-1">Payment</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <Field label="Payment terms">{row.payment_terms}</Field>
              <Field label="Payment method">{row.payment_method}</Field>
              <Field label="Due date type">{row.due_date_type}</Field>
              <Field label="Due (months / days)">
                {[row.due_date_months, row.due_date_days].filter((x) => x != null && x !== '').join(' / ') || '—'}
              </Field>
              <Field label="Central bank indicator">{row.central_bank_ind}</Field>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-sap-border/40 p-4">
            <div className="text-2xs font-bold text-sap-textLight uppercase tracking-wider mb-3 border-b border-sap-border/30 pb-1">Channel &amp; address</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <Field label="BP channel name">{row.bp_channel_name}</Field>
              <Field label="BP channel contact">{row.bp_channel_contact}</Field>
              <div className="sm:col-span-2">
                <Field label="Ship to"><span className="whitespace-pre-wrap text-sap-textLight">{row.ship_to}</span></Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Bill to"><span className="whitespace-pre-wrap text-sap-textLight">{row.bill_to}</span></Field>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
